import React from 'react'
import { useReactiveVar } from '@apollo/client'
import {
  Box,
  Divider,
  makeStyles,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { useTranslation } from 'react-i18next'
import {
  BuildingFragment,
  LocalGridFragment,
  LocalGridType,
} from '../../../generated/graphql'
import { activeVersionVar, projectVar } from '../../../apollo/reactive-vars'
import { useProviders } from '../../../provider/use-providers'
import { useLocalGridUtil } from '../../../local-grid/use-local-grid-util'
import { LocalGridCharts } from './LocalGridCharts'
import { SingleSettingSection } from '../../common/SingleSettingSection'
import { electricityProviderTypes } from '../../../provider/provider-properties'
import { useAuthorization } from '../../../authorization/use-authorization'
import { useProviderNames } from '../../../provider/use-provider-names'

const useStyles = makeStyles((theme) => ({
  waterGridConnected: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
  },
}))

type Props = {
  localGrid: LocalGridFragment
}

export function LocalGridBasicInfoTab({
  localGrid,
}: Props): React.ReactElement {
  const classes = useStyles()
  const project = useReactiveVar(projectVar)
  const activeVersion = useReactiveVar(activeVersionVar)
  const { createProvider, deleteProvider } = useProviders()
  const { typeToDefaultName } = useProviderNames(
    localGrid.providers.map((provider) => provider.name),
  )
  const { t } = useTranslation()
  const {
    providerType,
    waterGrid,
    connectedWaterGridProvider,
    isWaterGridConnected,
  } = useLocalGridUtil(localGrid)
  const canEditProject = useAuthorization().canEditProject(project)

  const allBuildings = activeVersion?.buildings ?? []
  const connectedBuildings = allBuildings.filter((building) =>
    building.providers.some((provider) => provider.type === providerType),
  )

  const onConnectBuildings = async (newBuildings: BuildingFragment[]) => {
    const newBuildingIds = newBuildings.map((building) => building.id)

    await Promise.all(
      connectedBuildings
        .filter((building) => !newBuildingIds.includes(building.id))
        .map((building) => {
          const provider = building.providers.find(
            (p) => p.type === providerType,
          )
          if (!provider) {
            return null
          }

          return deleteProvider(building, undefined, provider.id)
        }),
    )

    const connectedBuildingIds = connectedBuildings.map(
      (building) => building.id,
    )

    await Promise.all(
      newBuildings
        .filter((building) => !connectedBuildingIds.includes(building.id))
        .flatMap((building) =>
          createProvider(building, undefined, {
            name: typeToDefaultName(providerType),
            type: providerType,
            parameters: { inefficientDevice: { efficiency: 1 } },
            hourSeriesIds: [],
            performanceTableIds: [],
            temperatureCurveIds: [],
            demands: building.demands.map((demand) => ({
              demandId: demand.id,
              maxPortion: 1,
            })),
            providesElectricityForProviders:
              electricityProviderTypes.includes(providerType),
          }),
        ),
    )
  }

  const onToggleWaterGridConnection = async () => {
    if (connectedWaterGridProvider) {
      await deleteProvider(undefined, waterGrid, connectedWaterGridProvider.id)
      return
    }

    await createProvider(undefined, waterGrid, {
      name: t(`providerType.${providerType}`),
      type: providerType,
      parameters: { inefficientDevice: { efficiency: 1 } },
      hourSeriesIds: [],
      performanceTableIds: [],
      temperatureCurveIds: [],
      demands: [],
      providesElectricityForProviders:
        electricityProviderTypes.includes(providerType),
    })
  }

  return (
    <Box>
      {localGrid.type === LocalGridType.Electricity && waterGrid && (
        <>
          <SingleSettingSection
            title={t(
              'localGrid.Electricity.tabs.basicInfo.waterGridConnection',
            )}
          >
            <Box className={classes.waterGridConnected}>
              <Typography>
                {t('localGrid.Electricity.tabs.basicInfo.waterGridConnected')}
              </Typography>
              <Switch
                checked={isWaterGridConnected}
                onChange={onToggleWaterGridConnection}
                disabled={!canEditProject}
              />
            </Box>
          </SingleSettingSection>

          <Divider />
        </>
      )}

      <SingleSettingSection
        title={t('localGrid.tabs.basicInfo.connectedBuildings')}
      >
        <Autocomplete
          id="local-grid-buildings"
          options={allBuildings}
          getOptionLabel={(building) => building.name}
          getOptionSelected={(option, value) => option.id === value.id}
          value={connectedBuildings}
          onChange={(_, value) => onConnectBuildings(value)}
          renderInput={(params) => <TextField {...params} />}
          multiple
          disabled={!canEditProject}
        />
      </SingleSettingSection>

      <Divider />

      <LocalGridCharts localGrid={localGrid} />
    </Box>
  )
}
